import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Package, ShoppingCart, Mail, ArrowRight, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { useConfirm } from './ConfirmProvider';

/**
 * Supply Chain Risks Widget
 * Displays materials with availability issues and provides actions to resolve them.
 */
const SupplyChainRisksWidget = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [expandedAlert, setExpandedAlert] = useState(null);

  // Fetch supply chain risks
  const { data: risksData, isLoading } = useQuery({
    queryKey: ['supply-chain-risks'],
    queryFn: async () => {
      const response = await api.get('supply-chain/risks');
      return response.data;
    },
    refetchInterval: 300000, // Refetch every 5 minutes
  });

  // Find alternatives mutation
  const findAlternatives = useMutation({
    mutationFn: async (materialId) => {
      const response = await api.post('supply-chain/find-alternatives', {
        material_id: materialId,
        max_results: 3,
      });
      return response.data;
    },
  });

  // Reserve stock mutation
  const reserveStock = useMutation({
    mutationFn: async ({ materialId, projectId }) => {
      const response = await api.post('supply-chain/reserve-stock', {
        material_id: materialId,
        project_id: projectId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['supply-chain-risks']);
      toast.success(t('supplyChain.risks.voorraadGereserveerd', 'Voorraad gereserveerd'));
    },
  });

  // Send substitution notification mutation
  const sendNotification = useMutation({
    mutationFn: async ({ projectId, materialId, alternatives }) => {
      const response = await api.post('supply-chain/send-substitution-notification', {
        project_id: projectId,
        material_id: materialId,
        alternatives: alternatives,
        send_email: true,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('supplyChain.risks.notificatieVerzonden', 'Notificatie verzonden naar klant'));
    },
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow border border-line dark:border-gray-700 p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-20 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!risksData || risksData.total_risks === 0) {
    return null; // Don't show widget if no risks
  }

  const { red_alerts = [], orange_alerts = [] } = risksData;

  const handleReserveStock = async (materialId, projectId) => {
    if (await confirm({ message: t('supplyChain.risks.confirmReserveer', 'Weet je zeker dat je de resterende voorraad wilt reserveren voor dit project?') })) {
      reserveStock.mutate({ materialId, projectId });
    }
  };

  const handleFindAlternatives = async (materialId) => {
    try {
      const result = await findAlternatives.mutateAsync(materialId);
      setExpandedAlert({ materialId, alternatives: result.alternatives });
    } catch (error) {
      toast.error(t('supplyChain.risks.foutAlternatieven', 'Fout bij zoeken alternatieven'));
    }
  };

  const handleSendAlternatives = async (projectId, materialId, alternatives) => {
    if (await confirm({ message: t('supplyChain.risks.confirmStuur', 'Weet je zeker dat je de alternatieven naar de klant wilt sturen?') })) {
      sendNotification.mutate({ projectId, materialId, alternatives });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border-l-4 border-red-500 p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle className="w-6 h-6 text-red-500" />
        <h2 className="text-xl font-bold text-gray-900">
          ⚠️ {t('supplyChain.risks.title', "Supply Chain Risico's")}
        </h2>
        <span className="ml-auto px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
          {t('supplyChain.risks.risicoCount', '{{count}} risico(s)', { count: risksData.total_risks })}
        </span>
      </div>

      {/* Red Alerts (Discontinued/Out of Collection) */}
      {red_alerts.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-red-700 mb-3">
            🔴 {t('supplyChain.risks.kritiekeWaarschuwingen', 'Kritieke Waarschuwingen')} ({red_alerts.length})
          </h3>
          <div className="space-y-3">
            {red_alerts.map((alert) => (
              <div
                key={alert.material_id}
                className="bg-red-50 border border-red-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-red-900 mb-1">
                      {alert.material_name}
                    </h4>
                    {alert.sku && (
                      <p className="text-sm text-gray-600 mb-2">SKU: {alert.sku}</p>
                    )}
                    <p className="text-sm text-red-700 mb-2">
                      Status: {alert.status === 'DISCONTINUED' ? t('supplyChain.risks.uitProductie', 'Uit Productie') : t('supplyChain.risks.uitCollectie', 'Uit Collectie')}
                    </p>
                    {alert.impact && (
                      <p className="text-sm text-gray-600">
                        {t('supplyChain.risks.impact', 'Impact: {{projects}} project(en), {{quotes}} offerte(s)', { projects: alert.impact.projects_count, quotes: alert.impact.quotes_count })}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleFindAlternatives(alert.material_id)}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm flex items-center gap-1"
                      disabled={findAlternatives.isPending}
                    >
                      <Package className="w-4 h-4" />
                      {t('supplyChain.risks.zoekAlternatieven', 'Zoek Alternatieven')}
                    </button>
                    {alert.impact?.affected_project_ids?.[0] && (
                      <button
                        onClick={() =>
                          handleReserveStock(
                            alert.material_id,
                            alert.impact.affected_project_ids[0]
                          )
                        }
                        className="px-3 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm flex items-center gap-1"
                        disabled={reserveStock.isPending}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        {t('supplyChain.risks.reserveerVoorraad', 'Reserveer Voorraad')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded alternatives */}
                {expandedAlert?.materialId === alert.material_id && (
                  <div className="mt-4 pt-4 border-t border-red-200">
                    <h5 className="font-semibold text-gray-900 mb-2">{t('supplyChain.risks.gevondenAlternatieven', 'Gevonden Alternatieven:')}</h5>
                    <div className="space-y-2">
                      {expandedAlert.alternatives.map((alt) => (
                        <div
                          key={alt.material_id}
                          className="bg-white border border-gray-200 rounded p-3"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold">{alt.material_name}</p>
                              <p className="text-sm text-gray-600">
                                {alt.match_reasons.join(', ')}
                              </p>
                              {alt.price_difference_percent !== null && (
                                <p className="text-sm text-gray-600">
                                  {t('supplyChain.risks.prijsverschil', 'Prijsverschil')}: {alt.price_difference_percent > 0 ? '+' : ''}
                                  {alt.price_difference_percent}%
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {alt.availability_status === 'AVAILABLE' ? (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                              ) : (
                                <XCircle className="w-5 h-5 text-orange-500" />
                              )}
                            </div>
                          </div>
                          {alert.impact?.affected_project_ids?.[0] && (
                            <button
                              onClick={() =>
                                handleSendAlternatives(
                                  alert.impact.affected_project_ids[0],
                                  alert.material_id,
                                  expandedAlert.alternatives
                                )
                              }
                              className="mt-2 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-1"
                              disabled={sendNotification.isPending}
                            >
                              <Mail className="w-4 h-4" />
                              {t('supplyChain.risks.stuurAlternatieven', 'Stuur Alternatieven naar Klant')}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Orange Alerts (Low Stock) */}
      {orange_alerts.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-orange-700 mb-3">
            🟠 {t('supplyChain.risks.lageVoorraad', 'Lage Voorraad Waarschuwingen')} ({orange_alerts.length})
          </h3>
          <div className="space-y-2">
            {orange_alerts.map((alert) => (
              <div
                key={alert.material_id}
                className="bg-orange-50 border border-orange-200 rounded-lg p-3"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-orange-900">
                      {alert.material_name}
                    </h4>
                    {alert.current_stock !== undefined && alert.min_stock_level && (
                      <p className="text-sm text-gray-600">
                        {t('supplyChain.risks.huidigeVoorraad', 'Huidige voorraad: {{current}} (minimum: {{min}})', { current: alert.current_stock, min: alert.min_stock_level })}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      window.location.href = '/materialen';
                    }}
                    className="px-3 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm flex items-center gap-1"
                  >
                    {t('supplyChain.risks.bestelNu', 'Bestel Nu')}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplyChainRisksWidget;
